import * as React from 'react'
import { z } from 'zod'
import logger from '@/helpers/logger'

/**
 * How often to ask whether a newer build has been published.
 *
 * Five minutes. The file is a few hundred bytes and served from a CDN edge, so
 * the cost is negligible — but a survey runs on cell data, and there is no
 * reason to check more often than somebody might plausibly act on the answer.
 */
const POLL_MS = 5 * 60 * 1000

/**
 * What the deployed build publishes about itself. Written by
 * `scripts/write-version.mjs` after every build.
 */
const versionFileSchema = z.object({
  sha: z.string(),
  apiBaseUrl: z.string().nullable(),
  builtAt: z.string(),
})

/** Where a dismissal is remembered, keyed by the version dismissed. */
const DISMISSED_KEY = 'swocrm.updateDismissed'

/**
 * Whether a newer front end has been deployed than the one running.
 *
 * This exists because of a real incident. A laptop left open across a deploy
 * kept running the previous bundle, went on recording survey work in a format
 * the new API had stopped accepting, and reported seven changes lost. The
 * outbox now migrates its own old formats, but the general problem does not go
 * away: a long-lived tab is a version of the app nobody chose to keep running.
 *
 * Compares the SHA baked into this bundle against the one the server is
 * serving. Deliberately not a service worker or a websocket — a poll of a
 * static file has no state to get wrong, no registration to go stale, and
 * degrades to "no prompt" rather than to a broken page.
 */
export function useAppVersion(): {
  /** A newer build is live and this tab is not running it. */
  isStale: boolean
  /** The build now being served, once known. */
  latestSha: string | null
  dismiss: () => void
  refresh: () => void
} {
  const [latestSha, setLatestSha] = React.useState<string | null>(null)
  const [dismissedSha, setDismissedSha] = React.useState<string | null>(() => {
    try {
      return window.sessionStorage.getItem(DISMISSED_KEY)
    } catch {
      // Private browsing, blocked storage. Not being able to remember a
      // dismissal is a reason to prompt again, never a reason to fail.
      return null
    }
  })

  React.useEffect(() => {
    let canceled = false

    async function check(): Promise<void> {
      try {
        /*
         * `cache: 'no-store'`, or this asks a cache that was populated by the
         * build it is trying to notice has been replaced — and can never
         * return anything but the running version's own answer.
         */
        const response = await fetch('/version.json', { cache: 'no-store' })
        if (!response.ok) return
        const parsed = versionFileSchema.safeParse(await response.json())
        if (!parsed.success || canceled) return
        setLatestSha(parsed.data.sha)
      } catch {
        // Offline, or the file is not published in this environment. Neither
        // is worth a log line every five minutes on a phone in a park.
      }
    }

    void check()
    const timer = window.setInterval(() => {
      void check()
    }, POLL_MS)

    /*
     * Also on return to the tab. Somebody who left the laptop open over lunch
     * is exactly the case this exists for, and they should not wait out the
     * remainder of an interval to be told.
     */
    function onVisible(): void {
      if (document.visibilityState === 'visible') void check()
    }
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      canceled = true
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  const dismiss = React.useCallback(() => {
    if (latestSha === null) return
    setDismissedSha(latestSha)
    try {
      /*
       * Session storage, not local. Dismissing is "not now", not "never" — a
       * new tab tomorrow should ask again, and a dismissal is remembered
       * against the SHA so the NEXT deploy still prompts.
       */
      window.sessionStorage.setItem(DISMISSED_KEY, latestSha)
    } catch {
      // Remembering is a convenience; the prompt reappearing is the safe way
      // to fail.
    }
  }, [latestSha])

  const refresh = React.useCallback(() => {
    logger.info('Reloading for a newer build', { from: __APP_SHA__, to: latestSha })
    window.location.reload()
  }, [latestSha])

  /*
   * A local build reports "dev" and never matches a deployed SHA, which would
   * mean a permanent update prompt while developing. Comparing only when this
   * bundle knows its own commit keeps that out of the way.
   */
  const isStale =
    __APP_SHA__ !== 'dev' &&
    latestSha !== null &&
    latestSha !== __APP_SHA__ &&
    latestSha !== dismissedSha

  return { isStale, latestSha, dismiss, refresh }
}
